import { useState, useEffect } from 'react';
import { X, Upload, Package, User, Link2 } from 'lucide-react';
import { formatDateToISO } from '../utils/formatters';
import { toast } from 'react-toastify';
import { createExpense, updateExpense, getExpense, uploadReceipt } from '../api/expenses';
import { getExpenseCategories } from '../api/expenseCategories';
import { getDistributors, getDistributorOrders } from '../api/wholesale/distributors';

const ExpenseFormModal = ({
  isOpen,
  onClose,
  expenseId = null,
  onSuccess,
  module = 'retail',
  preSelectedOrderId = null,
  preSelectedDistributorId = null
}) => {
  const [loading, setLoading] = useState(false);
  const [categories, setCategories] = useState([]);
  const [formData, setFormData] = useState({
    category_id: '',
    amount: '',
    expense_date: formatDateToISO(new Date()),
    payment_method: 'cash',
    vendor_name: '',
    description: '',
    receipt_url: '',
    notes: '',
    status: 'pending',
    wholesale_order_id: ''
  });
  const [receiptFile, setReceiptFile] = useState(null);
  const [receiptPreview, setReceiptPreview] = useState('');
  const [uploadingReceipt, setUploadingReceipt] = useState(false);

  // Wholesale-specific state
  const [distributors, setDistributors] = useState([]);
  const [selectedDistributorId, setSelectedDistributorId] = useState('');
  const [distributorOrders, setDistributorOrders] = useState([]);
  const [loadingOrders, setLoadingOrders] = useState(false);

  const isEditMode = !!expenseId;
  const isWholesale = module === 'wholesale';

  useEffect(() => {
    if (isOpen) {
      fetchCategories();
      if (isWholesale) {
        fetchDistributors();
      }
      if (isEditMode) {
        fetchExpense();
      } else {
        resetForm();
        // Handle pre-selected order/distributor
        if (preSelectedDistributorId) {
          setSelectedDistributorId(preSelectedDistributorId.toString());
        }
        if (preSelectedOrderId) {
          setFormData((prev) => ({ ...prev, wholesale_order_id: preSelectedOrderId.toString() }));
        }
      }
    }
  }, [isOpen, expenseId]);

  // Fetch orders when distributor changes
  useEffect(() => {
    if (isWholesale && selectedDistributorId) {
      fetchDistributorOrders(selectedDistributorId);
    } else {
      setDistributorOrders([]);
    }
  }, [selectedDistributorId]);

  const fetchCategories = async () => {
    try {
      const response = await getExpenseCategories({ active_only: true });
      setCategories(response.data || []);
    } catch (error) {
      toast.error('Failed to load categories');
    }
  };

  const fetchDistributors = async () => {
    try {
      const response = await getDistributors({ limit: 1000, is_active: true });
      setDistributors(response.data || []);
    } catch (error) {
      console.error('Failed to load distributors:', error);
      // Don't block the UI, just log the error
    }
  };

  const fetchDistributorOrders = async (distributorId) => {
    try {
      setLoadingOrders(true);
      const response = await getDistributorOrders(distributorId, { limit: 100 });
      setDistributorOrders(response.data || []);
    } catch (error) {
      console.error('Failed to load distributor orders:', error);
      setDistributorOrders([]);
    } finally {
      setLoadingOrders(false);
    }
  };

  const fetchExpense = async () => {
    try {
      setLoading(true);
      const response = await getExpense(expenseId);
      const expense = response.data;

      setFormData({
        category_id: expense.category_id,
        amount: expense.amount,
        expense_date: expense.expense_date.split('T')[0],
        payment_method: expense.payment_method,
        vendor_name: expense.vendor_name || '',
        description: expense.description,
        receipt_url: expense.receipt_url || '',
        notes: expense.notes || '',
        status: expense.status,
        wholesale_order_id: expense.wholesale_order_id || ''
      });

      // If editing and has a linked order, we need to set the distributor
      if (expense.wholesale_order_id && expense.distributor_id) {
        setSelectedDistributorId(expense.distributor_id.toString());
      }

      if (expense.receipt_url) {
        setReceiptPreview(expense.receipt_url);
      }
    } catch (error) {
      toast.error('Failed to load expense');
      onClose();
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      category_id: '',
      amount: '',
      expense_date: formatDateToISO(new Date()),
      payment_method: 'cash',
      vendor_name: '',
      description: '',
      receipt_url: '',
      notes: '',
      status: 'pending',
      wholesale_order_id: ''
    });
    setReceiptFile(null);
    setReceiptPreview('');
    setSelectedDistributorId('');
    setDistributorOrders([]);
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'application/pdf'];
      if (!validTypes.includes(file.type)) {
        toast.error('Please upload a valid file (JPG, PNG, or PDF)');
        return;
      }

      if (file.size > 5 * 1024 * 1024) {
        toast.error('File size must be less than 5MB');
        return;
      }

      setReceiptFile(file);

      if (file.type.startsWith('image/')) {
        const reader = new FileReader();
        reader.onloadend = () => {
          setReceiptPreview(reader.result);
        };
        reader.readAsDataURL(file);
      } else {
        setReceiptPreview('PDF file selected');
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.category_id) {
      toast.error('Please select a category');
      return;
    }

    if (!formData.amount || parseFloat(formData.amount) <= 0) {
      toast.error('Please enter a valid amount');
      return;
    }

    if (!formData.description.trim()) {
      toast.error('Please enter a description');
      return;
    }

    try {
      setLoading(true);

      let receipt_url = formData.receipt_url;

      // Upload receipt file if one is selected
      if (receiptFile) {
        try {
          setUploadingReceipt(true);
          const uploadResponse = await uploadReceipt(receiptFile);
          if (uploadResponse.success && uploadResponse.data?.receipt_url) {
            receipt_url = uploadResponse.data.receipt_url;
          } else {
            throw new Error('Upload failed');
          }
        } catch (uploadErr) {
          console.error('Receipt upload error:', uploadErr);
          toast.error('Failed to upload receipt. Creating expense without receipt.');
          // Continue without receipt if upload fails
        } finally {
          setUploadingReceipt(false);
        }
      }

      const expenseData = {
        ...formData,
        amount: parseFloat(formData.amount),
        receipt_url,
        module: module // Matches backend expenseService.createExpense parameter
      };

      if (isEditMode) {
        await updateExpense(expenseId, expenseData);
        toast.success('Expense updated successfully');
      } else {
        await createExpense(expenseData);
        toast.success('Expense created successfully');
      }

      onSuccess();
      onClose();
      resetForm();
    } catch (error) {
      console.error('Expense creation error:', error);
      toast.error(
        error.response?.data?.message || `Failed to ${isEditMode ? 'update' : 'create'} expense`
      );
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value
    }));
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 overflow-y-auto">
      <div className="bg-white rounded-lg max-w-3xl w-full my-8">
        {/* Modal Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">
              {isEditMode ? 'Edit Expense' : 'New Expense'}
            </h2>
            <p className="text-gray-600 text-sm mt-1">
              {isEditMode ? 'Update expense details' : 'Submit a new expense for approval'}
            </p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 transition-colors">
            <X size={24} />
          </button>
        </div>

        {/* Modal Body */}
        <form onSubmit={handleSubmit}>
          <div className="p-6 space-y-4 max-h-[calc(100vh-300px)] overflow-y-auto">
            {/* Category and Amount */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Category *</label>
                <select
                  name="category_id"
                  value={formData.category_id}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-black focus:border-transparent"
                  required
                >
                  <option value="">Select a category</option>
                  {categories.map((category) => (
                    <option key={category.id} value={category.id}>
                      {category.name}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Amount (₦) *</label>
                <input
                  type="number"
                  name="amount"
                  value={formData.amount}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-black focus:border-transparent"
                  placeholder="0.00"
                  step="0.01"
                  min="0.01"
                  required
                />
              </div>
            </div>

            {/* Wholesale Order Linking Section */}
            {isWholesale && (
              <div className="bg-emerald-50 dark:bg-emerald-900/80 border border-emerald-200 dark:border-emerald-800/30 rounded-lg p-4">
                <div className="flex items-center gap-2 mb-3">
                  <Link2 size={18} className="text-emerald-600 dark:text-emerald-400" />
                  <h3 className="font-medium text-emerald-800 dark:text-emerald-300">
                    Link to Order (Optional)
                  </h3>
                </div>
                <p className="text-sm text-emerald-700 dark:text-emerald-400/80 mb-4">
                  Link this expense to a specific order to track true order costs for accounting.
                  This will not affect customer documents (Invoice, Receipt).
                </p>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {/* Customer/Distributor Selection */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      <User size={14} className="inline mr-1" />
                      Customer
                    </label>
                    <select
                      value={selectedDistributorId}
                      onChange={(e) => {
                        setSelectedDistributorId(e.target.value);
                        setFormData((prev) => ({ ...prev, wholesale_order_id: '' }));
                      }}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                    >
                      <option value="">Select a customer</option>
                      {distributors.map((dist) => (
                        <option key={dist.id} value={dist.id}>
                          {dist.business_name || dist.contact_name}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* Order Selection */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      <Package size={14} className="inline mr-1" />
                      Order
                    </label>
                    <select
                      name="wholesale_order_id"
                      value={formData.wholesale_order_id}
                      onChange={handleChange}
                      disabled={!selectedDistributorId || loadingOrders}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent disabled:bg-gray-100 dark:disabled:bg-gray-800 disabled:cursor-not-allowed dark:bg-gray-700 dark:text-white"
                    >
                      <option value="">
                        {loadingOrders
                          ? 'Loading orders...'
                          : !selectedDistributorId
                            ? 'Select customer first'
                            : 'Select an order'}
                      </option>
                      {distributorOrders.map((order) => (
                        <option key={order.id} value={order.id}>
                          {order.order_number} - ₦{Number(order.total_amount || 0).toLocaleString()}{' '}
                          ({order.order_status})
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                {/* Selected Order Details */}
                {formData.wholesale_order_id && distributorOrders.length > 0 && (
                  <div className="mt-3 p-3 bg-white dark:bg-gray-800/50 rounded border border-emerald-200 dark:border-emerald-800/30">
                    {(() => {
                      const selectedOrder = distributorOrders.find(
                        (o) => o.id.toString() === formData.wholesale_order_id
                      );
                      if (!selectedOrder) return null;
                      return (
                        <div className="flex justify-between text-sm">
                          <div>
                            <span className="font-medium text-gray-900 dark:text-white">
                              {selectedOrder.order_number}
                            </span>
                            <span className="text-gray-500 dark:text-gray-400 ml-2">
                              {new Date(selectedOrder.order_date).toLocaleDateString()}
                            </span>
                          </div>
                          <div className="text-right">
                            <span className="font-medium text-gray-900 dark:text-white">
                              ₦{Number(selectedOrder.total_amount || 0).toLocaleString()}
                            </span>
                            <span
                              className={`ml-2 px-2 py-0.5 rounded text-xs ${
                                selectedOrder.order_status === 'completed'
                                  ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300'
                                  : selectedOrder.order_status === 'delivered'
                                    ? 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300'
                                    : 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300'
                              }`}
                            >
                              {selectedOrder.order_status}
                            </span>
                          </div>
                        </div>
                      );
                    })()}
                  </div>
                )}
              </div>
            )}

            {/* Date and Payment Method */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Expense Date *
                </label>
                <input
                  type="date"
                  name="expense_date"
                  value={formData.expense_date}
                  onChange={handleChange}
                  max={formatDateToISO(new Date())}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-black focus:border-transparent"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Payment Method *
                </label>
                <select
                  name="payment_method"
                  value={formData.payment_method}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-black focus:border-transparent"
                  required
                >
                  <option value="cash">Cash</option>
                  <option value="bank_transfer">Bank Transfer</option>
                  <option value="card">Card</option>
                  <option value="cheque">Cheque</option>
                  <option value="mobile_money">Mobile Money</option>
                  <option value="other">Other</option>
                </select>
              </div>
            </div>

            {/* Vendor Name */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Vendor/Payee Name
              </label>
              <input
                type="text"
                name="vendor_name"
                value={formData.vendor_name}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-black focus:border-transparent"
                placeholder="Enter vendor or payee name"
              />
            </div>

            {/* Description */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Description *</label>
              <textarea
                name="description"
                value={formData.description}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-black focus:border-transparent"
                rows="3"
                placeholder="Provide a detailed description of the expense"
                required
              />
            </div>

            {/* Receipt Upload */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Receipt/Invoice
              </label>
              <div className="mt-1 flex justify-center px-6 pt-5 pb-6 border-2 border-gray-300 border-dashed rounded-lg hover:border-gray-400 transition-colors">
                <div className="space-y-1 text-center">
                  {receiptPreview ? (
                    <div className="relative">
                      {receiptPreview.startsWith('data:image') ||
                      receiptPreview.startsWith('blob:') ? (
                        <img
                          src={receiptPreview}
                          alt="Receipt preview"
                          className="mx-auto h-32 w-auto rounded"
                        />
                      ) : (
                        <div className="flex items-center justify-center h-32">
                          <p className="text-sm text-gray-600">{receiptPreview}</p>
                        </div>
                      )}
                      <button
                        type="button"
                        onClick={() => {
                          setReceiptFile(null);
                          setReceiptPreview('');
                          setFormData((prev) => ({ ...prev, receipt_url: '' }));
                        }}
                        className="absolute top-0 right-0 bg-red-500 text-white rounded-full p-1 hover:bg-red-600"
                      >
                        <X size={16} />
                      </button>
                    </div>
                  ) : (
                    <>
                      <Upload className="mx-auto h-12 w-12 text-gray-400" />
                      <div className="flex text-sm text-gray-600">
                        <label
                          htmlFor="receipt-upload-modal"
                          className="relative cursor-pointer bg-white rounded-md font-medium text-black hover:text-gray-800"
                        >
                          <span>Upload a file</span>
                          <input
                            id="receipt-upload-modal"
                            type="file"
                            className="sr-only"
                            accept="image/*,.pdf"
                            onChange={handleFileChange}
                          />
                        </label>
                        <p className="pl-1">or drag and drop</p>
                      </div>
                      <p className="text-xs text-gray-500">PNG, JPG, PDF up to 5MB</p>
                    </>
                  )}
                </div>
              </div>
            </div>

            {/* Notes */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Additional Notes
              </label>
              <textarea
                name="notes"
                value={formData.notes}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-black focus:border-transparent"
                rows="2"
                placeholder="Any additional information (optional)"
              />
            </div>
          </div>

          {/* Modal Footer */}
          <div className="px-6 py-4 bg-gray-50 border-t border-gray-200 flex gap-3">
            <button
              type="submit"
              disabled={loading || uploadingReceipt}
              className="flex-1 bg-black text-white px-4 py-2 rounded-lg hover:bg-gray-800 transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed"
            >
              {uploadingReceipt
                ? 'Uploading Receipt...'
                : loading
                  ? 'Saving...'
                  : isEditMode
                    ? 'Update Expense'
                    : 'Submit Expense'}
            </button>
            <button
              type="button"
              onClick={onClose}
              disabled={loading}
              className="flex-1 bg-gray-200 text-gray-800 px-4 py-2 rounded-lg hover:bg-gray-300 transition-colors disabled:cursor-not-allowed"
            >
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ExpenseFormModal;
